import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, CreditCard, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { StripePaymentElement } from "@/components/StripePaymentElement";

interface SavedCardResponse {
  has_card: boolean;
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
}

interface Props {
  listingId: string;
  /** Shown under the heading so the buyer can see what they're paying for. */
  listingTitle?: string | null;
  onClose: () => void;
  /** Fired once the unlock is recorded — the host navigates to the listing. */
  onUnlocked: (listingId: string) => void;
}

/** "Visa ···· 4242" — Stripe returns the brand lowercased. */
function cardLabel(card: SavedCardResponse): string {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : "Card";
  return `${brand} ···· ${card.last4 ?? "••••"}`;
}

function expiryLabel(card: SavedCardResponse): string | null {
  if (!card.exp_month || !card.exp_year) return null;
  return `Exp ${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`;
}

/**
 * Paid-unlock checkout, as a popup over whatever page the buyer was on.
 *
 * The card already on file is pre-selected so unlocking is a single click that
 * shows only the last 4 digits — the full Stripe card form is a fallback for
 * buyers with no saved card (or who pick "Use a different card"), and that form
 * saves its card so the next unlock is one-click too.
 */
export function UnlockListingModal({ listingId, listingTitle, onClose, onUnlocked }: Props) {
  const toast = useToast();

  const [amount, setAmount] = useState<number | null>(null);
  const [savedCard, setSavedCard] = useState<SavedCardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [useNewCard, setUseNewCard] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The price is the buyer's tier `extra_listing_amount`; the saved card is
    // display-only (the server re-resolves the payment method when charging).
    Promise.all([
      api.get<{ extra_listing_amount: number | null }>("/tiers/my-unlocked-listings"),
      api.get<SavedCardResponse>("/payments/saved-card").catch(() => ({ has_card: false })),
    ])
      .then(([quota, card]) => {
        if (cancelled) return;
        setAmount(quota.extra_listing_amount);
        setSavedCard(card.has_card ? card : null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load payment details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  // Lock the page behind the popup so the marketplace can't scroll under it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const finish = () => {
    setDone(true);
    toast.success("Listing unlocked!");
    setTimeout(() => onUnlocked(listingId), 900);
  };

  const payWithSavedCard = async () => {
    setPaying(true);
    setPayError("");
    try {
      const res = await api.post<{
        success: boolean;
        requires_action?: boolean;
        client_secret?: string;
        payment_intent_id?: string;
      }>("/payments/unlock-with-saved-card", { listing_id: listingId });

      // 3-D Secure: finish the challenge in-page, then record the unlock.
      if (res.requires_action && res.client_secret) {
        const stripe = (window as any).Stripe;
        if (!stripe) throw new Error("Card verification is unavailable. Please use a different card.");
        const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: res.client_secret });
        if (error) throw new Error(error.message ?? "Card verification failed. Please try again.");
        await api.post(`/payments/confirm-unlock/${listingId}`, { payment_intent_id: paymentIntent.id });
      }
      finish();
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const createUnlockIntent = async (): Promise<string> => {
    const res = await api.post<{ client_secret: string }>("/payments/create-unlock-intent", {
      listing_id: listingId,
    });
    return res.client_secret;
  };

  const handleNewCardSuccess = async (paymentIntentId: string) => {
    await api.post(`/payments/confirm-unlock/${listingId}`, { payment_intent_id: paymentIntentId });
    finish();
  };

  const busy = paying || done;
  const showSavedCard = savedCard !== null && !useNewCard;

  /* Portalled to <body> so the overlay always covers the nav and sidebar — the
     hosting page column would otherwise confine it — and z-[100] clears the nav
     (z-50) and the sidebar toggle (z-60). */
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={() => !busy && onClose()}
    >
      {/* max-h-full + an internal scroll region: the Stripe card form (with Link
          signup and extra payment methods) is far taller than the saved-card view
          and would otherwise push the header off the top of the screen. Height
          comes from the padded inset-0 parent, never a vh unit the app zoom scales. */}
      <div
        className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-6 pt-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              {done ? <CheckCircle className="h-5 w-5 text-emerald-500" /> : <Lock className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-foreground">
                {done ? "Unlocked!" : "Unlock this listing"}
              </h3>
              {listingTitle && !done && (
                <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{listingTitle}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => !busy && onClose()}
            disabled={busy}
            className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
        {done ? (
          <p className="mt-5 text-center text-sm text-muted-foreground">Opening the full listing…</p>
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading payment details…
          </div>
        ) : loadError || amount == null ? (
          <p className="py-8 text-center text-sm text-destructive">
            {loadError || "Could not load payment details. Please try again."}
          </p>
        ) : (
          <>
            <div className="mt-5 flex items-center justify-between rounded-xl border border-border/70 bg-muted/40 px-4 py-3">
              <span className="text-sm text-muted-foreground">One-time payment</span>
              <span className="text-xl font-extrabold text-foreground">${amount}</span>
            </div>

            {showSavedCard ? (
              <div className="mt-4 space-y-3">
                <p className="text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">
                  Paying with
                </p>
                <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3">
                  <CreditCard className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">{cardLabel(savedCard!)}</p>
                    {expiryLabel(savedCard!) && (
                      <p className="text-xs text-muted-foreground">{expiryLabel(savedCard!)}</p>
                    )}
                  </div>
                  <span className="ml-auto shrink-0 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-primary">
                    Saved
                  </span>
                </div>

                {payError && <p className="text-xs text-destructive">{payError}</p>}

                <button
                  onClick={payWithSavedCard}
                  disabled={paying}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gold-gradient text-sm font-bold text-primary-foreground shadow-md transition-[filter] hover:brightness-[1.06] disabled:opacity-60"
                >
                  {paying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      Pay ${amount} &amp; Unlock
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setPayError("");
                    setUseNewCard(true);
                  }}
                  disabled={paying}
                  className="w-full text-center text-xs font-semibold text-primary underline-offset-2 hover:underline disabled:opacity-60"
                >
                  Use a different card
                </button>

                <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                    256-bit encryption
                  </span>
                  <span className="text-border">|</span>
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Powered by Stripe
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <StripePaymentElement
                  amount={amount}
                  createIntent={createUnlockIntent}
                  buttonLabel={`Pay $${amount} & Unlock`}
                  onSuccess={handleNewCardSuccess}
                  saveCard
                />
                {savedCard && (
                  <button
                    onClick={() => setUseNewCard(false)}
                    className="w-full text-center text-xs font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    Use my saved card ({cardLabel(savedCard)})
                  </button>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
